/**
 * Pre-fills Microchannel-rendered D365 Marketing form fields from our
 * authenticated member context payload.
 *
 * Microchannel's FormLoader.bundle.js does NOT read the `data-prefill-*`
 * attributes that some embed snippets set on the wrapper div — those are a
 * no-op left over from a different integration approach. The supported
 * mechanism is the `d365mkt-afterformload` event the bundle dispatches on
 * `window.d365mktforms`. After the form renders, we walk its `<input>`,
 * `<select>` and `<textarea>` elements and set values for any whose `name`
 * attribute matches a known D365 schema name in our context.
 *
 * Each context field can target multiple possible input names — Microchannel
 * publishes different forms (My Details, Organisation Details, etc.) that
 * sometimes use schema variants (`emailaddress1` on the contact form,
 * `emailaddress2` on the org form). Listing both is harmless: only the inputs
 * actually present in the rendered form receive a value.
 */

// Map context-field key (camelCase from our API) → array of input `name`
// candidates. Each candidate must match the rendered field's `name` attribute
// EXACTLY (note: `name` often differs from the field's `data-targetproperty`,
// e.g. data-targetproperty="company" but name="companyPhone1").
//
// Verified against the four live member forms (My Profile / Membership Details /
// Organisation Details / Organisation Information). The same logical value can
// have different `name`s across forms (e.g. organisation name is
// `nds_primaryaffiliatedorgstatic` on My Profile but `adx_organizationname` on
// Organisation Details) — listing both is safe: only inputs present in a given
// form get a value.
//
// Field mappings verified against docs/NDS - Website Advisory - API Details.xlsx
// (sheets: My Profile, Membership Details, Organisation Details,
// Organisation Information, Renewal). Each context key maps to one or more
// rendered form `name` attributes — Microchannel often differs from the D365
// schema name (e.g. mcs_WorkplaceAssistCode vs mcs_workplaceassistcode).
const FIELD_NAME_MAP = {
  // contact identity (My Profile + staff registration forms). The membership-application
  // form prefixes the same fields `appContact_*` for its "Application Contact details"
  // block — the applicant themselves. Its `mainContact_*` fields are deliberately NOT
  // mapped: that block is a different person (typically the CEO or General Manager) and
  // the form asks whether the applicant is them.
  // The staff registration form carries the two D365 ids as `mcs_`-prefixed inputs so the
  // submission can be tied back to the contact/account records.
  contactId: ['contactid', 'mcs_contactid'],
  firstName: ['firstname', 'appContact_firstname'],
  lastName: ['lastname', 'appContact_lastname'],
  mainContactFullName: ['fullname'],
  // login email (emailaddress1) + business email (emailaddress2) on My Profile; the
  // application form's Application Contact work email is emailaddress2.
  // Do NOT prefill `EmailOfPersonCompletingTheForm` — the renewal form's "email of
  // person completing this form" must be entered manually by the member.
  email: ['emailaddress1', 'emailaddress2', 'email'],
  mobile: ['mobilephone', 'appContact_mobile'],
  // contact-level work phone (D365 contact.telephone1) — distinct from the
  // organisation phone below; the My Profile form names this input `business2`.
  workPhone: ['business2', 'appContact_phone'],
  title: ['mcs_title'], // option-set <select> — matched by option label
  positionTitle: ['jobtitle', 'appContact_position'],
  state: ['address1_stateorprovince'],

  // membership-level (Membership Details form — see xlsx "Membership Details" sheet)
  adminContactFullName: ['AdminContact', 'mcs_admincontactname', 'mcs_admincontactfullname'],
  // account.nds_precrmmembernumber
  membershipNumber: ['membershipNumber', 'nds_precrmmembernumber'],
  memberSince: ['joinedDate'], // mcs_membership.mcs_joineddate — ISO yyyy-MM-dd
  // mcs_membershipcategory.mcs_name (not mcs_membershiptype)
  currentMembershipType: ['membershipType', 'nds_hl_orgmembershipcategory', 'mcs_membershiptype'],
  memberCategory: ['membershipType', 'nds_hl_orgmembershipcategory', 'mcs_membershiptype'],
  // nds_memberhsipfeeslookup → mcs_membershipfee.mcs_name (Renewal sheet row 25)
  currentMembershipFeeType: ['MembershipFeeType', 'nds_membershipfee', 'mcs_membershipfee'],
  // account.mcs_workplaceassistcode / mcs_workplaceassistadviceline
  workplaceAssistCode: ['mcs_WorkplaceAssistCode', 'mcs_workplaceassistcode', 'workplaceAssistCode'],
  workplaceAssistAdviceLine: ['mcs_WorkplaceAssistAdviceLine', 'mcs_workplaceassistadviceline', 'workplaceAssistAdviceLine'],

  // organisation / account (Organisation Details + Renewal sheets)
  providerId: ['accountid', 'mcs_providerid'],
  organisationName: ['adx_organizationname', 'nds_primaryaffiliatedorgstatic'],
  organisationAbn: ['organizationabn', 'mcs_abn2'],
  abnEntityName: ['abnentityname', 'nds_abrentityname'],
  // account.mcs_sageerpcustomercode
  customerCode: ['customerCode', 'mcs_sageerpcustomercode'],
  organisationPhone: ['companyPhone1', 'telephone1', 'company'],
  website: ['websiteurl2', 'websiteurl'],
  linkedInUrl: ['mcs_linkedin', 'mcs_linkedinurl'],
  postalAddressLine1: ['address1_line1'],
  postalAddressLine2: ['address1_line2'],
  postalAddressCity: ['address1_city'],
  postalAddressState: ['mcs_state', 'address1_stateorprovince'], // <select>, matched by label
  postalAddressPostcode: ['address1_postalcode'],
  streetAddressLine1: ['address2_line1'],
  streetAddressLine2: ['address2_line2'],
  streetAddressCity: ['address2_city'],
  streetAddressState: ['mcs_streetstate', 'street_state', 'address2_stateorprovince'],
  streetAddressPostcode: ['address2_postalcode'],
  // "Street address same as postal?" — bool, renders as a Yes/No radio (or single
  // checkbox); handled by the checkbox/radio branch of setFieldValue.
  streetSameAsPostal: ['mcs_isstreetaddresssameaspostaladdress'],

  // organisation information form
  ndisRegistered: ['NDIS_Commission', 'mcs_ndisregistered'],
  fteStaffCount: ['NoOfETEstaff', 'mcs_numberofemployeesdec'],
  currentClientsCount: ['NoOfClients', 'nds_totalcurrentclients'],
  organisationNote: ['about_organisation', 'mcs_notefromapplicant'],
  totalAnnualIncome: ['revenue', 'mcs_revenue'],
  disabilityIncome: ['mcs_revenuefromdisabilityservicesprovisiononl'],

  // Multi-value relationship fields (Organisation Information form). The API returns
  // these as arrays; setFieldValue routes arrays to setMultiValue, which ticks every
  // matching checkbox by its label/value. Rendered `name`s below are verified against
  // the live form (checkbox groups). Matching is by the checkbox's visible label, so
  // the D365 value must equal the label text:
  //   - disability types: labels are full names (e.g. "Physical") — matches mcs_disabilitytype. ✓
  //   - services: labels are the service names (e.g. "Transport Services"). Some D365
  //     service-type names may not exactly equal a form label (taxonomy drift) — those
  //     won't tick until aligned. Names-only (client counts not prefilled).
  //   - states: form labels are abbreviations (ACT, NSW, …). If mcs_stateterritory.mcs_name
  //     is a full state name this won't match — revisit with a real account that has states.
  statesOperateIn: ['States_operate', 'mcs_stateterritory'],
  disabilityTypesServiced: ['disabilitytype', 'mcs_disabilitytype'],
  servicesProvided: ['servicesprovided', 'nds_clientsbyservicetype'],
}

/**
 * Sets an input/select/textarea's value and dispatches `input` + `change`
 * events so any internal form state (validation, dirty tracking) updates.
 */
function setFieldValue(field, value) {
  if (field == null || value == null || value === '') return false

  // Multi-value fields (states operated in, disability types, services provided)
  // arrive as arrays — tick/select every matching option rather than stringifying.
  if (Array.isArray(value)) {
    return value.length > 0 && setMultiValue(field, value)
  }

  // <select> (option-set): our context carries the label (e.g. "NSW", "Ms"),
  // but the option values are numeric D365 codes — match by value first, then
  // by visible option text.
  if (field.tagName === 'SELECT') {
    return setSelectValue(field, value)
  }

  // Radio / checkbox option groups. A boolean choice (e.g. "Are you registered
  // with the NDIS Commission?") renders as a Yes/No pair sharing one `name`, so
  // we select the option matching the value and clear the other — never blanket
  // "check if truthy", which would select both. Each option is identified by its
  // `title`/label FIRST, because D365 sometimes emits a useless `value` (the NDIS
  // field renders BOTH Yes and No with value="false"); value is only a last
  // resort, and a lone checkbox with no Yes/No token falls back to "check when truthy".
  if (field.type === 'checkbox' || field.type === 'radio') {
    const truthy = value === true || value === 'true' || value === 1 || value === '1'
    const token = (field.title || getOptionLabelText(field) || field.value || '').trim().toLowerCase()
    let desired
    if (token === 'yes' || token === 'true' || token === '1') desired = truthy
    else if (token === 'no' || token === 'false' || token === '0') desired = !truthy
    else desired = truthy
    if (field.checked === desired) return false
    field.checked = desired
  } else {
    const stringValue = String(value)
    if (field.value === stringValue) return false
    field.value = stringValue
  }

  field.dispatchEvent(new Event('input', { bubbles: true }))
  field.dispatchEvent(new Event('change', { bubbles: true }))
  return true
}

/**
 * Reads the visible label text for a radio/checkbox option so a Yes/No pair can
 * be told apart when the option `value` attribute isn't meaningful (the NDIS
 * field renders both options with value="false"). Tries the wrapping <label>,
 * then a `for=`-linked label, then the immediate next-sibling text.
 */
function getOptionLabelText(field) {
  const wrapping = field.closest('label')
  if (wrapping?.textContent) return wrapping.textContent
  if (field.id) {
    const linked = field.ownerDocument?.querySelector(`label[for="${field.id}"]`)
    if (linked?.textContent) return linked.textContent
  }
  const sibling = field.nextElementSibling
  if (sibling?.textContent) return sibling.textContent
  return ''
}

/** Selects the option matching `value` by option value, then by label text. */
function setSelectValue(select, value) {
  const target = String(value).trim().toLowerCase()
  const options = Array.from(select.options)
  const match =
    options.find((option) => option.value.toLowerCase() === target) ||
    options.find((option) => (option.textContent || '').trim().toLowerCase() === target)

  if (!match || select.value === match.value) return false
  select.value = match.value
  select.dispatchEvent(new Event('input', { bubbles: true }))
  select.dispatchEvent(new Event('change', { bubbles: true }))
  return true
}

/** Fires `input` + `change` so form validation / dirty-tracking picks up the change. */
function dispatchFieldEvents(field) {
  field.dispatchEvent(new Event('input', { bubbles: true }))
  field.dispatchEvent(new Event('change', { bubbles: true }))
}

/**
 * Applies an array of values to one field of a multi-value control. Because
 * `applyPrefillToForm` calls this once per element sharing the target `name`, a
 * checkbox group resolves correctly: each checkbox is ticked only when its own
 * value/label is one of `values`. A `<select multiple>` gets every match selected;
 * a plain input falls back to a comma-joined string.
 */
function setMultiValue(field, values) {
  const wanted = values
    .map((v) => String(v).trim().toLowerCase())
    .filter(Boolean)
  if (wanted.length === 0) return false

  if (field.tagName === 'SELECT') {
    let changed = false
    if (field.multiple) {
      Array.from(field.options).forEach((option) => {
        const desired =
          wanted.includes(option.value.trim().toLowerCase()) ||
          wanted.includes((option.textContent || '').trim().toLowerCase())
        if (option.selected !== desired) {
          option.selected = desired
          changed = true
        }
      })
    } else {
      const option = Array.from(field.options).find(
        (o) =>
          wanted.includes(o.value.trim().toLowerCase()) ||
          wanted.includes((o.textContent || '').trim().toLowerCase()),
      )
      if (option && field.value !== option.value) {
        field.value = option.value
        changed = true
      }
    }
    if (changed) dispatchFieldEvents(field)
    return changed
  }

  if (field.type === 'checkbox' || field.type === 'radio') {
    const valueToken = (field.value || '').trim().toLowerCase()
    const labelToken = (getOptionLabelText(field) || field.title || '')
      .trim()
      .toLowerCase()
    const desired =
      (valueToken !== '' && wanted.includes(valueToken)) ||
      (labelToken !== '' && wanted.includes(labelToken))
    if (field.checked === desired) return false
    field.checked = desired
    dispatchFieldEvents(field)
    return true
  }

  // Plain text/textarea fallback: join for display.
  const joined = values.join(', ')
  if (field.value === joined) return false
  field.value = joined
  dispatchFieldEvents(field)
  return true
}

/**
 * Walks a rendered form's fields and applies values from `memberContext`.
 * Returns the count of fields actually populated (useful for diagnostics).
 */
export function applyPrefillToForm(formRoot, memberContext) {
  if (!formRoot || !memberContext) return 0

  let filled = 0
  Object.entries(FIELD_NAME_MAP).forEach(([contextKey, inputNames]) => {
    const value = memberContext[contextKey]
    if (value == null || value === '') return

    inputNames.forEach((inputName) => {
      const fields = formRoot.querySelectorAll(
        `[name="${inputName}"]`,
      )
      fields.forEach((field) => {
        if (setFieldValue(field, value)) {
          filled += 1
        }
      })
    })
  })
  return filled
}

/**
 * Registers a single global `d365mkt-afterformload` listener that pre-fills
 * any form rendered on the current page from `memberContext`. Idempotent —
 * the per-page listener is replaced each time so updates to `memberContext`
 * are honoured. The bundle's API surface only becomes available after
 * `FormLoader.bundle.js` has executed, so this is called from within
 * components that already wait for the script to be ready.
 */
export function registerPrefillHandler(memberContext) {
  if (typeof window === 'undefined') return
  const api = window.d365mktforms
  if (!api || typeof api.addEventListener !== 'function') return

  // Detach any prior handler we attached (so re-renders with a fresh context
  // don't accumulate listeners and don't reapply stale values).
  if (window.__ndsD365PrefillHandler) {
    api.removeEventListener?.('d365mkt-afterformload', window.__ndsD365PrefillHandler)
  }

  const handler = (event) => {
    // Event detail shape varies between bundle versions; try the documented
    // `formElement` first and fall back to the event target.
    const formRoot = event?.detail?.formElement ?? event?.target ?? document
    applyPrefillToForm(formRoot, memberContext)
  }

  window.__ndsD365PrefillHandler = handler
  api.addEventListener('d365mkt-afterformload', handler)
}

// Poll schedule (ms after submit) for re-syncing the Umbraco cache from D365.
// The re-sync re-reads D365, but a Marketing-form submission ingests
// asynchronously and can take a while to land on the contact/account records.
// A single early read predates the write and would persist the pre-edit value
// (member then only sees the change after their next login). So we re-sync with
// backoff and stop as soon as the snapshot actually changes. Total window ~2 min
// (NDS-313: ingest regularly exceeded the previous ~37s window, so the final
// reload showed the pre-edit value); if D365 is slower still, the next login
// reconciles as before.
const REFRESH_POLL_DELAYS_MS = [2500, 5000, 10000, 20000, 30000, 30000, 30000]

/**
 * Serialises a context for change-detection, ignoring the always-updated
 * `lastSyncedAt` stamp so an unchanged sync isn't mistaken for a real edit.
 */
function normaliseForCompare(ctx) {
  if (!ctx) return null
  const { lastSyncedAt, ...rest } = ctx
  return JSON.stringify(rest)
}

/**
 * Reverse of FIELD_NAME_MAP: a submitted field's `key` (the D365 form input name) → the context
 * field key(s) it feeds. Built once so a submission payload can be turned into our context shape.
 */
const INPUT_NAME_TO_CONTEXT_KEYS = Object.entries(FIELD_NAME_MAP).reduce((map, [contextKey, inputNames]) => {
  inputNames.forEach((name) => {
    if (!map[name]) map[name] = []
    map[name].push(contextKey)
  })
  return map
}, {})

/**
 * Parses one field from the `d365mkt-formsubmit` payload into the shape our /current context uses,
 * so the two compare directly:
 *   - multi-value (services, states, disability types): `details.valueLabel` is a JSON array of
 *     { value, label } → take the discrete labels (a comma inside a label survives intact).
 *   - option-set / Yes-No (e.g. NDIS): `details.valueLabel` is a plain string ("Yes") → use it.
 *   - text / number: no valueLabel → use the raw `value`.
 */
function parseSubmittedField(field) {
  let details = {}
  try {
    details = JSON.parse(field.details || '{}')
  } catch {
    details = {}
  }
  const valueLabel = details.valueLabel
  if (typeof valueLabel === 'string') {
    const trimmed = valueLabel.trim()
    if (trimmed.startsWith('[')) {
      try {
        const pairs = JSON.parse(trimmed)
        if (Array.isArray(pairs)) return pairs.map((pair) => pair?.label).filter((label) => label != null)
      } catch {
        // Malformed valueLabel — fall through to the raw value.
      }
    } else if (trimmed !== '') {
      return trimmed
    }
  }
  return field.value ?? ''
}

/**
 * Turns a `d365mkt-formsubmit` event detail into the member's submitted values, keyed like our
 * /current context, so the post-submit poll knows exactly what to wait for. Reads the authoritative
 * event payload (exact codes + discrete labels) rather than scraping the DOM, which under-reads
 * checkbox groups and can't represent a label that itself contains a comma.
 */
function captureSubmittedContextValues(detail) {
  const captured = {}
  const fields = detail?.payload?.fields
  if (!Array.isArray(fields)) return captured
  fields.forEach((field) => {
    const contextKeys = field?.key ? INPUT_NAME_TO_CONTEXT_KEYS[field.key] : null
    if (!contextKeys) return
    const value = parseSubmittedField(field)
    contextKeys.forEach((key) => {
      captured[key] = value
    })
  })
  return captured
}

/**
 * Splits a multi-value field into a normalised set. Accepts an array (how /current returns these
 * fields) OR a comma-joined string (how a checkbox group can serialise when read from the DOM), so
 * the two representations compare equal.
 */
function toValueSet(value) {
  const parts = Array.isArray(value) ? value : String(value ?? '').split(',')
  return new Set(parts.map((v) => String(v).trim().toLowerCase()).filter(Boolean))
}

/** Normalises a boolean-ish value (true/false, "Yes"/"No", "1"/"0"). */
function toBool(value) {
  if (typeof value === 'boolean') return value
  const s = String(value).trim().toLowerCase()
  return s === 'true' || s === 'yes' || s === '1'
}

/** Normalises a scalar so "11" == 11 == 11.0 and case/whitespace don't matter. */
function toScalar(value) {
  if (value == null) return ''
  const s = String(value).trim()
  const num = Number(s)
  return s !== '' && !Number.isNaN(num) ? String(num) : s.toLowerCase()
}

/**
 * True when a refreshed-context value matches the member's submitted value for that field. Handles
 * the three shapes our fields take: multi-value (array from /current vs. array OR comma-joined
 * string from the DOM capture) compared as order-insensitive sets; boolean (Yes/No); and scalars
 * (numeric-tolerant, case-insensitive).
 */
function valueMatches(contextValue, submittedValue) {
  if (Array.isArray(contextValue) || Array.isArray(submittedValue)) {
    const a = toValueSet(contextValue)
    const b = toValueSet(submittedValue)
    if (a.size !== b.size) return false
    for (const item of b) {
      if (!a.has(item)) return false
    }
    return true
  }
  if (typeof contextValue === 'boolean' || typeof submittedValue === 'boolean') {
    return toBool(contextValue) === toBool(submittedValue)
  }
  return toScalar(contextValue) === toScalar(submittedValue)
}

/**
 * Diagnostic logging for the post-submit refresh poll is OFF by default in EVERY environment,
 * production included. Enable it per-browser for staging verification with `?d365debug=1` in the
 * URL (persisted to localStorage so it survives the post-submit reload), or by running
 * `localStorage.setItem('ndsD365Debug', '1')` in the console. `?d365debug=0` (or removing the
 * localStorage key) turns it back off. Because staging and production ship the same bundle, this
 * runtime opt-in is the only reliable way to keep production silent.
 */
function d365DebugEnabled() {
  if (typeof window === 'undefined') return false
  try {
    const flag = new URLSearchParams(window.location.search).get('d365debug')
    if (flag === '1') window.localStorage?.setItem('ndsD365Debug', '1')
    else if (flag === '0') window.localStorage?.removeItem('ndsD365Debug')
    return window.localStorage?.getItem('ndsD365Debug') === '1'
  } catch {
    return false
  }
}

/**
 * Gated on the opt-in flag so nothing is logged in production by default. Uses console.info (not
 * console.debug) because Chrome hides debug/verbose-level messages unless the console log-level
 * dropdown is switched to "Verbose" — info shows at the default level.
 */
function debugLog(message) {
  if (d365DebugEnabled()) console.info(message)
}

/**
 * Builds the readiness check that decides when the refreshed snapshot is safe to reload on. We
 * watch only the fields the member actually changed (submitted value differs from the pre-submit
 * baseline) and require every one of them to be reflected in D365 — so a slow-landing field can't
 * reload the page while it is still mid-ingestion. If nothing was captured (e.g. the form shape
 * was unexpected), we fall back to the previous "any change from baseline" behaviour so the reload
 * can never hang beyond the poll schedule / safety timeout.
 *
 * Returns { watchKeys, pending(context) } where pending() lists the still-unreflected fields, so
 * the poller can both decide readiness (pending is empty) and log progress for staging diagnosis.
 */
function buildReadiness(baseline, captured) {
  const watchKeys = Object.keys(captured).filter((key) => !valueMatches(baseline?.[key], captured[key]))
  if (watchKeys.length === 0) {
    const base = normaliseForCompare(baseline)
    return {
      watchKeys: [],
      captured,
      pending: (context) => (normaliseForCompare(context) !== base ? [] : ['(any change)']),
    }
  }
  return {
    watchKeys,
    captured,
    pending: (context) => watchKeys.filter((key) => !valueMatches(context[key], captured[key])),
  }
}

/**
 * Renders a captured/refreshed value for the diagnostic log. Arrays join on " | " (not ", ") so a
 * label that itself contains a comma — e.g. "Advocacy, Information Services" — stays countable.
 */
function formatValue(value) {
  if (Array.isArray(value)) return `[${value.join(' | ')}]`
  if (value == null || value === '') return '(none)'
  return String(value)
}

function pollRefresh(refreshUrl, readiness, onRefreshed, attempt) {
  const isLast = attempt >= REFRESH_POLL_DELAYS_MS.length - 1
  fetch(refreshUrl, { method: 'POST', credentials: 'same-origin' })
    .then((res) => (res.ok ? res.json() : null))
    .then((context) => {
      // Reload only once the snapshot reflects every field the member changed (pending is empty),
      // so a slow-landing field (e.g. the services N:N relationship) can't trigger a reload while
      // its value is still mid-ingestion. isLast is the timeout backstop — we always reload eventually.
      const pending = context ? readiness.pending(context) : ['(no snapshot)']
      const ready = !!context && pending.length === 0
      const outcome = ready ? 'ready → reload' : isLast ? 'timeout → reload' : 'waiting'
      // Show expected (submitted) vs got (refreshed) per still-pending field so it's clear whether
      // D365 is still ingesting (got is empty/partial) or the values simply don't match (drift).
      const pendingDetail = pending
        .map((key) =>
          context && key in context
            ? `${key}: expected=${formatValue(readiness.captured[key])} got=${formatValue(context[key])}`
            : key,
        )
        .join('; ')
      debugLog(
        `[d365FormPrefill] refresh poll ${attempt + 1}/${REFRESH_POLL_DELAYS_MS.length} — ` +
          `pending: ${pendingDetail || 'none'} — ${outcome}`,
      )
      if (context && (ready || isLast)) {
        onRefreshed?.(context)
        return
      }
      if (!isLast) scheduleRefresh(refreshUrl, readiness, onRefreshed, attempt + 1)
    })
    .catch((err) => {
      console.error('[d365FormPrefill] Member cache refresh failed:', err)
      if (!isLast) scheduleRefresh(refreshUrl, readiness, onRefreshed, attempt + 1)
    })
}

function scheduleRefresh(refreshUrl, readiness, onRefreshed, attempt) {
  window.setTimeout(
    () => pollRefresh(refreshUrl, readiness, onRefreshed, attempt),
    REFRESH_POLL_DELAYS_MS[attempt],
  )
}

/**
 * Registers `d365mkt-formsubmit` (capture) + `d365mkt-afterformsubmit` (poll) listeners so that,
 * after a member submits an embedded D365 form, we re-sync their Umbraco cache from D365 and hand
 * the fresh snapshot to `onRefreshed`. This fixes the stale-prefill problem: the form writes to
 * D365, but the cache that drives prefill was previously only refreshed on login (see
 * MemberContextSurfaceController).
 *
 * Because D365 ingests a multi-field submit asynchronously and field-by-field (the services N:N
 * relationship lands noticeably slower than the scalar fields), we capture the fields the member
 * changed from the DOM at submit time and poll with backoff (see REFRESH_POLL_DELAYS_MS) until the
 * refreshed snapshot reflects EVERY changed field — never reloading on the first field to land,
 * which would strand a slow field mid-ingestion. The last poll attempt reloads regardless, so the
 * page always comes back.
 *
 * Idempotent — the per-page listeners are replaced each call. Registered on both the bundle API
 * and `document`, mirroring the load-event registration in formAnalytics, because the submit event
 * surfaces on either depending on bundle version.
 *
 * @param {string} refreshUrl POST endpoint that re-syncs and returns the fresh snapshot.
 * @param {(context: object) => void} onRefreshed Receives the refreshed member context.
 * @param {object} [baselineContext] Pre-submit snapshot; used to tell which captured fields the
 *   member actually changed (so unchanged fields aren't waited on).
 * @param {(container: Element|null) => void} [onSubmitStart] Fires the moment a form is
 *   submitted (before polling begins), with the submitted form's [data-form-id] wrapper,
 *   so the caller can cover D365's "form submitted" view with a placeholder until the
 *   poll-driven reload lands. Resolved from the same event detail the load handler uses.
 */
export function registerRefreshOnSubmit(refreshUrl, onRefreshed, baselineContext, onSubmitStart) {
  if (typeof window === 'undefined' || !refreshUrl) return
  const api = window.d365mktforms

  if (window.__ndsD365SubmitCaptureHandler) {
    api?.removeEventListener?.('d365mkt-formsubmit', window.__ndsD365SubmitCaptureHandler)
    document.removeEventListener('d365mkt-formsubmit', window.__ndsD365SubmitCaptureHandler)
  }
  if (window.__ndsD365SubmitRefreshHandler) {
    api?.removeEventListener?.('d365mkt-afterformsubmit', window.__ndsD365SubmitRefreshHandler)
    document.removeEventListener('d365mkt-afterformsubmit', window.__ndsD365SubmitRefreshHandler)
  }

  // d365mkt-formsubmit fires BEFORE D365 replaces the form with its "submitted" view, so the
  // member's chosen values still exist in the DOM. Capture them here (keyed like /current) so the
  // post-submit poll knows exactly which fields to wait for — the afterformsubmit event's own
  // payload shape is undocumented and unreliable.
  let capturedValues = {}
  const captureHandler = (event) => {
    // d365mkt-formsubmit carries the authoritative submission at event.detail.payload.fields —
    // exact option codes plus discrete labels — which we parse into our context shape.
    capturedValues = captureSubmittedContextValues(event?.detail)
  }

  const submitHandler = (event) => {
    if (typeof onSubmitStart === 'function') {
      const formEl = event?.detail?.formElement ?? event?.target ?? null
      onSubmitStart(formEl?.closest?.('[data-form-id]') ?? null)
    }
    const readiness = buildReadiness(baselineContext, capturedValues)
    const watchDetail = readiness.watchKeys
      .map((key) => `${key}=${formatValue(readiness.captured[key])}`)
      .join('; ')
    debugLog(
      `[d365FormPrefill] submit captured ${Object.keys(capturedValues).length} field(s); ` +
        `waiting on changed field(s): ${watchDetail || '(any change — capture empty)'}`,
    )
    scheduleRefresh(refreshUrl, readiness, onRefreshed, 0)
  }

  window.__ndsD365SubmitCaptureHandler = captureHandler
  window.__ndsD365SubmitRefreshHandler = submitHandler
  api?.addEventListener?.('d365mkt-formsubmit', captureHandler)
  document.addEventListener('d365mkt-formsubmit', captureHandler)
  api?.addEventListener?.('d365mkt-afterformsubmit', submitHandler)
  document.addEventListener('d365mkt-afterformsubmit', submitHandler)

  // One-time load-time confirmation (registerRefreshOnSubmit runs on every form page and is
  // retried a few times while the bundle loads, so dedupe): tells the tester the new bundle and
  // the debug flag are both live and the handlers are attached, before any form submit.
  if (d365DebugEnabled() && !window.__ndsD365RefreshArmedLogged) {
    window.__ndsD365RefreshArmedLogged = true
    console.info('[d365FormPrefill] refresh-on-submit armed — submit the form to see field-readiness polling')
  }
}
